import { LatLng, LatLngBounds } from 'leaflet';
import { useState } from 'react';
import { isMobileVersion } from '../../../../lib/soar-helper';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ApiAnalytics from '../../../../api/api-analytics';
import { AnalyticsAction, SatelliteProvider } from '../../../../api/model';
import { compareSentinelFeatures } from '../../../../lib/sentinel-service';
import Analytics from '../../../../lib/user-analytics';
import { actionFlyToOnMap } from '../../../../store/App/actions';
import {
    actionSentinelChangeOpacity,
    actionSentinelEndBoxSelect,
    actionSentinelOpenDownload,
    actionSentinelOpenShare,
    actionSentinelSelectFeature,
} from '../../../../store/Map/Sentinel/actions';
import { SentinelFeature } from '../../../../store/Map/Sentinel/model';
import { selectSentinelError, selectSentinelOpacity } from '../../../../store/Map/Sentinel/selectors';

const moment = require('moment');

interface SentinelItemProps {
    feature: SentinelFeature;
    selectedFeature?: SentinelFeature;
}

interface ContainerProps {
    isSelected: boolean;
}

const Detail = (props: { name: string; value: string; isSelected: boolean }) => {
    return (
        <DetailRow>
            <DetailName isSelected={props.isSelected} isMobile={isMobileVersion}>
                {props.name}:
            </DetailName>
            <DetailValue>{props.value}</DetailValue>
        </DetailRow>
    );
};

const SentinelItem = (props: SentinelItemProps) => {
    const sentinelOpacity = useSelector(selectSentinelOpacity);
    const sentinelError = useSelector(selectSentinelError);

    const dispatch = useDispatch();
    const openDownload = () => dispatch(actionSentinelOpenDownload());
    const changeOpacity = (opacity: number) => dispatch(actionSentinelChangeOpacity({ id: props.feature.id, opacity }));
    const flyTo = (position: LatLng | LatLngBounds) => dispatch(actionFlyToOnMap(position));
    const openShare = () => dispatch(actionSentinelOpenShare());

    const [previewImageFailed, setPreviewImageFailed] = useState<boolean>(false);
    const opacity = sentinelOpacity[props.feature.id] || 1.0;

    const layer = () => {
        switch (props.feature.layer) {
            case 'TRUE_COLOR_PREVIEW':
            case 'TRUE_COLOR':
                return 'True Color';
            case 'FALSE_COLOR':
                return 'False Color';
            case 'GEOLOGY':
                return 'Geology';
            case 'NDVI':
                return 'NDVI';
            case 'SENTINEL_1_RADAR':
            case 'SENTINEL_1_GRD':
                return 'Radar';
            case 'LANDSAT_8':
                return 'Landsat 8';
            default:
                return '';
        }
    };

    const isSelected =
        (props.selectedFeature && compareSentinelFeatures(props.feature, props.selectedFeature)) ||
        // On mobile we only display one result and not all results but need the isSelected to
        // trigger as true to display the sharelink and transparency slider for the item
        (isMobileVersion && props.selectedFeature)
            ? true
            : false;

    return (
        <Container
            data-testid="landsat-feature"
            isSelected={isSelected}
            onClick={() => {
                // Fixes #4876 - Forces the sentinel feature to toggle to undefined before setting to allow updates of deeply nested state re-render
                dispatch(actionSentinelSelectFeature(undefined));
                requestAnimationFrame(() => {
                    dispatch(actionSentinelSelectFeature(props.feature));
                });
                dispatch(actionSentinelEndBoxSelect());
                flyTo(props.feature.bbox);
                Analytics.Event('Satellite - Sentinel', 'Selected sentinel feature', props.feature.bbox.toBBoxString());
                ApiAnalytics.postAnalyticsSatellite(
                    props.feature.previewUrl,
                    SatelliteProvider.SINERGISE,
                    props.feature.satelliteAnalytics,
                    AnalyticsAction.VIEW
                );
            }}
        >
            <PreviewImage
                src={
                    previewImageFailed ? '/assets/image-unavailable/preview_unavailable.png' : props.feature.previewUrl
                }
                onError={() => setPreviewImageFailed(true)}
            />
            <DetailsContainer>
                <Detail isSelected={isSelected} name="Date" value={moment(props.feature.date).format('MMM Do YYYY')} />
                <Detail isSelected={isSelected} name="Layer" value={layer()} />
                <Detail
                    isSelected={isSelected}
                    name={isMobileVersion ? 'Resolution' : 'Pixel Resolution'}
                    value={props.feature.resolution}
                />
                {isSelected && (
                    <DetailRow>
                        <DetailName isSelected={isSelected}>Transparency: </DetailName>
                        <DetailValue style={{ marginTop: '-3px' }}>
                            <Slider
                                percentageFilled={opacity * 100}
                                type="range"
                                defaultValue={100}
                                value={opacity * 100}
                                step={0.01}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    const opacity = parseInt(e.target.value) / 100;
                                    changeOpacity(opacity || 0.0001);
                                }}
                            />
                        </DetailValue>
                    </DetailRow>
                )}
            </DetailsContainer>
            {isSelected ? (
                <ButtonContainer isDisabled={sentinelError ? true : false}>
                    <DownloadButton
                        title="Download"
                        onClick={() => {
                            openDownload();
                            Analytics.Event(
                                'Satellite - Sentinel',
                                'Clicked download',
                                props.feature.bbox.toBBoxString()
                            );
                        }}
                        disabled={sentinelError ? true : false}
                    >
                        <DownloadIcon src="/assets/floating-drawer-icons/download-icon.png" />
                    </DownloadButton>
                    <ShareButton
                        title="Share"
                        onClick={() => {
                            openShare();
                            Analytics.Event('Satellite - Sentinel', 'Clicked share', props.feature.bbox.toBBoxString());
                        }}
                        disabled={sentinelError ? true : false}
                    >
                        <ShareIcon src="/assets/floating-drawer-icons/share-icon.png" />
                    </ShareButton>
                </ButtonContainer>
            ) : null}
        </Container>
    );
};

export default SentinelItem;

const Container = styled.div<ContainerProps>`
    user-select: none;
    height: ${(props) => (props.isSelected ? '105px' : '93px')};
    min-height: 93px;
    background: ${(props) => (props.isSelected ? '#353535' : 'transparent')};
    padding: 12px;
    cursor: ${(props) => (props.isSelected ? 'cursor' : 'pointer')};
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    flex-direction: row;
    &:hover {
        background: ${(props) => (props.isSelected ? '#353535' : '#545454')};
    }

    @media (max-width: 800px) {
        background: transparent;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        &:hover {
            background: transparent;
        }
    }
`;

const PreviewImage = styled.img`
    user-select: none;
    height: 64px;
    min-width: 64px;
    width: auto;
    border-radius: 8px;
`;

const DetailsContainer = styled.div`
    user-select: none;
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const DetailRow = styled.div`
    user-select: none;
    display: flex;
    flex-direction: row;
    margin: 0 0 0 12px;
    padding: 0;
`;

interface IsSelectedProps {
    isSelected: boolean;
    isMobile?: boolean;
}

const DetailName = styled.span<IsSelectedProps>`
    color: rgb(176, 176, 176);
    font-size: 1rem;
    width: ${(props) => (props.isMobile ? '80px' : '113px')};
`;

const DetailValue = styled.span`
    color: white;
    font-size: 1rem;
`;

interface SliderProps {
    percentageFilled: number;
}

const Slider = styled.input<SliderProps>`
    background-color: #eed926 !important;
    background: ${(props) =>
        `linear-gradient(to right, #eed926 0%, #eed926 ${props.percentageFilled}%, #999 ${props.percentageFilled}%, #999 100%);`};
    height: 2px;
    margin: -4px 0 0 0;
    box-shadow: none !important;
    border: none !important;
    -webkit-appearance: none;
    outline: none;
    border-radius: 5px;
    cursor: pointer;

    &::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        background: #eed926;

        height: 12px;
        width: 12px;
        border-radius: 50%;
    }

    &:hover::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        background: #eed926;
        border-radius: 50%;
        cursor: pointer;
        -webkit-box-shadow: 0px 0px 10px 2px rgba(238, 217, 38, 0.69);
        -moz-box-shadow: 0px 0px 10px 2px rgba(238, 217, 38, 0.69);
        box-shadow: 0px 0px 10px 2px rgba(238, 217, 38, 0.69);
    }

    @media (max-width: 800px) {
        width: 85%;
    }
`;

interface ButtonProps {
    isDisabled?: boolean;
}

const ButtonContainer = styled.div<ButtonProps>`
    display: flex;
    flex-direction: column;
    opacity: ${(props) => props.isDisabled && 0.6};
`;

const DownloadButton = styled.button`
    margin: 0 auto;
    margin-bottom: 4px;
    display: block;
    width: 42px;
    height: 30px;
    font-size: 1rem;
    background-color: #eed926 !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: black;
    border-radius: 4px;

    @media (max-width: 650px) {
        display: none;
    }
`;

const DownloadIcon = styled.img`
    margin: 4px 4px 4px 4px;
`;

const ShareButton = styled.button`
    margin: 0 auto;
    margin-bottom: 4px;
    display: block;
    width: 42px;
    height: 30px;
    font-size: 1rem;
    background-color: #eed926 !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: black;
    border-radius: 4px;

    @media (max-width: 650px) {
        margin-top: 20px;
    }
`;

const ShareIcon = styled.img`
    margin: 4px 3px 4px 3px;
`;
